import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaUpload, FaFile, FaFilePdf, FaFileImage, FaFileExcel, FaFileWord, FaTrash } from 'react-icons/fa';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './AddIncomeModal.module.css';

const initialFormData = {
  GibNumarasi: '',
  TaslakNo: '',
  Senaryo: '',
  FaturaTipi: '',
  OlusturmaTarihi: '',
  OlusturmaZamani: '',
  BelgeTarihi: '',
  BelgeZamani: '',
  MusteriTcknVkn: '',
  MusteriUnvan: '',
  MusteriEtiketi: '',
  Tutar: '',
  ParaBirimi: 'TRY',
  ZarfUuid: '',
  ZarfDurumu: '',
  Ettn: '',
  Iskonto: '',
  FaturaDisiMasraflari: '',
  SatirSayisi: '',
  BirimFiyatMiktarTutari: '',
  OdenecekTutar: '',
  VergilerHaricTutar: '',
  VergilerDahilTutar: '',
  ÖdemeDurumu: 'Alındı',
  AktifDurum: 'Aktif',
};

const toNum = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const AddIncomeModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const getFileIcon = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return <FaFileImage />;
    if (ext === 'pdf') return <FaFilePdf />;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return <FaFileExcel />;
    if (['doc', 'docx'].includes(ext)) return <FaFileWord />;
    return <FaFile />;
  };

  const handleFileSelect = (files) => {
    const newFiles = Array.from(files).filter(file => !selectedFiles.some(f => f.name === file.name && f.size === file.size));
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };
  const handleFileInputChange = (e) => { handleFileSelect(e.target.files); e.target.value = ''; };
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); handleFileSelect(e.dataTransfer.files); };
  const removeFile = (index) => setSelectedFiles(prev => prev.filter((_, i) => i !== index));

  const uploadFiles = async (docId) => {
    const uploaded = [];
    for (const file of selectedFiles) {
      const storageRef = ref(storage, `budgets/income/${docId}/${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      uploaded.push({ name: file.name, url, size: file.size });
    }
    return uploaded;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setSelectedFiles([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const unvan = String(formData.MusteriUnvan || '').trim();
    const gib = String(formData.GibNumarasi || '').trim();
    const tutar = toNum(formData.Tutar);
    const belgeTarihi = formData.BelgeTarihi || formData.OlusturmaTarihi || '';

    const dataToSubmit = {
      ...formData,
      Tutar: tutar,
      Iskonto: toNum(formData.Iskonto),
      FaturaDisiMasraflari: toNum(formData.FaturaDisiMasraflari),
      SatirSayisi: toNum(formData.SatirSayisi),
      BirimFiyatMiktarTutari: toNum(formData.BirimFiyatMiktarTutari),
      OdenecekTutar: toNum(formData.OdenecekTutar),
      VergilerHaricTutar: toNum(formData.VergilerHaricTutar),
      VergilerDahilTutar: toNum(formData.VergilerDahilTutar),
      BütçeAdı: gib ? `${unvan} - ${gib}` : unvan || 'Gelir',
      BütçeKaynak: unvan,
      BütçeSahibi: 'Şirket',
      BütçeTipi: 'Gelir',
      GiderTipi: 'Gelir',
      BütçeKategorisi: 'E-Fatura',
      KDV: '0',
      BütçeMiktarı: tutar,
      BütçeTarihi: belgeTarihi,
      BeklenenÖdemeTarihi: belgeTarihi,
      createdAt: new Date().toISOString(),
      importSource: 'manual',
    };

    try {
      const docRef = await addDoc(collection(db, 'Budgets'), dataToSubmit);
      if (selectedFiles.length > 0) {
        const fileURLs = await uploadFiles(docRef.id);
        await updateDoc(doc(db, 'Budgets', docRef.id), { attachments: fileURLs });
        dataToSubmit.attachments = fileURLs;
      }
      toast.success('Gelir başarıyla eklendi!');
      handleReset();
      onSubmit && onSubmit(dataToSubmit);
      onClose();
    } catch (error) {
      console.error('Error adding income:', error);
      toast.error('Gelir eklenirken bir hata oluştu!');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Yeni Gelir Ekle</h2>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close modal">
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          <form onSubmit={handleSubmit}>
            <div className={styles.sectionTitle}>Fatura Bilgileri</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>GİB Numarası</label>
                <input type="text" name="GibNumarasi" value={formData.GibNumarasi} onChange={handleChange} className={styles.input} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Taslak No</label>
                <input type="text" name="TaslakNo" value={formData.TaslakNo} onChange={handleChange} className={styles.input} />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Senaryo</label>
                <input type="text" name="Senaryo" value={formData.Senaryo} onChange={handleChange} className={styles.input} placeholder="Ticari, Temel vb." />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Fatura Tipi</label>
                <input type="text" name="FaturaTipi" value={formData.FaturaTipi} onChange={handleChange} className={styles.input} placeholder="SATIS, TEVKIFAT vb." />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>ETTN</label>
                <input type="text" name="Ettn" value={formData.Ettn} onChange={handleChange} className={styles.input} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Zarf UUID</label>
                <input type="text" name="ZarfUuid" value={formData.ZarfUuid} onChange={handleChange} className={styles.input} />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Zarf Durumu</label>
                <input type="text" name="ZarfDurumu" value={formData.ZarfDurumu} onChange={handleChange} className={styles.input} />
              </div>
              <div className={styles.formGroup} />
            </div>

            <div className={styles.sectionTitle}>Tarih & Zaman</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Oluşturma Tarihi</label>
                <input type="date" name="OlusturmaTarihi" value={formData.OlusturmaTarihi} onChange={handleChange} className={styles.input} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Oluşturma Zamanı</label>
                <input type="time" step="1" name="OlusturmaZamani" value={formData.OlusturmaZamani} onChange={handleChange} className={styles.input} />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Belge Tarihi *</label>
                <input type="date" name="BelgeTarihi" value={formData.BelgeTarihi} onChange={handleChange} className={styles.input} required />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Belge Zamanı</label>
                <input type="time" step="1" name="BelgeZamani" value={formData.BelgeZamani} onChange={handleChange} className={styles.input} />
              </div>
            </div>

            <div className={styles.sectionTitle}>Müşteri Bilgileri</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Müşteri TCKN/VKN</label>
                <input type="text" name="MusteriTcknVkn" value={formData.MusteriTcknVkn} onChange={handleChange} className={styles.input} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Müşteri Unvan *</label>
                <input type="text" name="MusteriUnvan" value={formData.MusteriUnvan} onChange={handleChange} className={styles.input} required placeholder="Unvan" />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Müşteri Etiketi</label>
                <input type="text" name="MusteriEtiketi" value={formData.MusteriEtiketi} onChange={handleChange} className={styles.input} />
              </div>
              <div className={styles.formGroup} />
            </div>

            <div className={styles.sectionTitle}>Tutar Bilgileri</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Tutar *</label>
                <input type="number" step="0.01" name="Tutar" value={formData.Tutar} onChange={handleChange} className={styles.input} required />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Para Birimi</label>
                <select name="ParaBirimi" value={formData.ParaBirimi} onChange={handleChange} className={styles.select}>
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>İskonto</label>
                <input type="number" step="0.01" name="Iskonto" value={formData.Iskonto} onChange={handleChange} className={styles.input} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Fatura Dışı Masraflar</label>
                <input type="number" step="0.01" name="FaturaDisiMasraflari" value={formData.FaturaDisiMasraflari} onChange={handleChange} className={styles.input} />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Satır Sayısı</label>
                <input type="number" step="1" name="SatirSayisi" value={formData.SatirSayisi} onChange={handleChange} className={styles.input} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Birim Fiyat * Miktar Tutarı</label>
                <input type="number" step="0.01" name="BirimFiyatMiktarTutari" value={formData.BirimFiyatMiktarTutari} onChange={handleChange} className={styles.input} />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Ödenecek Tutar</label>
                <input type="number" step="0.01" name="OdenecekTutar" value={formData.OdenecekTutar} onChange={handleChange} className={styles.input} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Vergiler Hariç Tutar</label>
                <input type="number" step="0.01" name="VergilerHaricTutar" value={formData.VergilerHaricTutar} onChange={handleChange} className={styles.input} />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Vergiler Dahil Tutar</label>
                <input type="number" step="0.01" name="VergilerDahilTutar" value={formData.VergilerDahilTutar} onChange={handleChange} className={styles.input} />
              </div>
              <div className={styles.formGroup} />
            </div>

            <div className={styles.sectionTitle}>Durum</div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Ödeme Durumu</label>
                <select name="ÖdemeDurumu" value={formData.ÖdemeDurumu} onChange={handleChange} className={styles.select}>
                  <option value="Alındı">Alındı</option>
                  <option value="Beklemede">Beklemede</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Aktif Durum</label>
                <select name="AktifDurum" value={formData.AktifDurum} onChange={handleChange} className={styles.select}>
                  <option value="Aktif">Aktif</option>
                  <option value="Pasif">Pasif</option>
                </select>
              </div>
            </div>

            <div className={styles.sectionTitle}>Dosya Ekle (İsteğe Bağlı)</div>
            <div className={styles.formGroup}>
              <div
                className={`${styles.dropZone} ${isDragging ? styles.dropZoneDragging : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current.click()}
              >
                <FaUpload className={styles.uploadIcon} />
                <p className={styles.dropZoneText}>Dosyaları buraya sürükleyin veya tıklayın</p>
                <p className={styles.dropZoneHint}>PDF, Word, Excel, resim ve diğer dosyalar</p>
                <input ref={fileInputRef} type="file" multiple onChange={handleFileInputChange} className={styles.hiddenInput} accept="*/*" />
              </div>
              {selectedFiles.length > 0 && (
                <div className={styles.fileList}>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className={styles.fileItem}>
                      <span className={styles.fileIcon}>{getFileIcon(file)}</span>
                      <span className={styles.fileName}>{file.name}</span>
                      <span className={styles.fileSize}>({(file.size / 1024).toFixed(1)} KB)</span>
                      <button type="button" className={styles.removeFileBtn} onClick={() => removeFile(index)}>
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.buttonGroup}>
              <button type="button" className={`${styles.button} ${styles.cancelButton}`} onClick={onClose}>İptal</button>
              <button type="button" className={`${styles.button} ${styles.resetButton}`} onClick={handleReset}>Sıfırla</button>
              <button type="submit" className={`${styles.button} ${styles.submitButton}`} disabled={isSubmitting}>
                {isSubmitting ? 'Ekleniyor...' : 'Gelir Ekle'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddIncomeModal;
